import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ChatContext = createContext(null);
const WS_URL = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/chat`;

// One live socket per signed-in tab: presence, new messages, typing and call signalling.
export function ChatProvider({ children }) {
    const { user } = useAuth();
    const [online, setOnline] = useState(new Set());
    const [unread, setUnread] = useState(0);
    const [connected, setConnected] = useState(false);
    const wsRef = useRef(null);
    const listeners = useRef(new Set());
    const callRef = useRef(null);

    const refreshUnread = useCallback(() => {
        api.get("/chat/unread-count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!user?.id) return undefined;
        let closed = false;
        let retry = 0;
        let timer;
        let ping;
        const connect = () => {
            const ws = new WebSocket(WS_URL);
            wsRef.current = ws;
            ws.onopen = () => {
                retry = 0;
                setConnected(true);
                ping = setInterval(() => ws.readyState === 1 && ws.send('{"type":"ping"}'), 25000);
            };
            ws.onmessage = (e) => {
                const m = JSON.parse(e.data);
                if (m.type === "hello") setOnline(new Set(m.online));
                if (m.type === "presence") setOnline((s) => { const n = new Set(s); m.online ? n.add(m.user_id) : n.delete(m.user_id); return n; });
                if (m.type === "message" && m.message.sender_id !== user.id) refreshUnread();
                listeners.current.forEach((fn) => fn(m));
            };
            ws.onclose = (e) => {
                clearInterval(ping);
                setConnected(false);
                if (!closed && e.code !== 4401) timer = setTimeout(connect, Math.min(15000, 1000 * 2 ** retry++));
            };
        };
        connect();
        refreshUnread();
        return () => { closed = true; clearTimeout(timer); clearInterval(ping); wsRef.current?.close(); };
    }, [user?.id, refreshUnread]);

    const send = useCallback((obj) => {
        const ws = wsRef.current;
        if (ws?.readyState !== 1) return false;
        ws.send(JSON.stringify(obj));
        return true;
    }, []);
    const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
    const startCall = useCallback((peer, kind) => callRef.current?.(peer, kind), []);

    return (
        <ChatContext.Provider value={{ me: user, online, unread, connected, refreshUnread, send, subscribe, startCall, callRef }}>
            {children}
        </ChatContext.Provider>
    );
}

export const useChat = () => useContext(ChatContext);
